/**
 * The init.js holds functions for initialization of the EnviDat app
 *
 * @summary init.js
 * @author Dominik Haas-Artho
 *
 * Created at     : 2019-10-23 16:12:30
 * Last modified  : 2021-01-06 16:30:10
 *
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.txt', which is part of this source code package. */

import axios, { InternalAxiosRequestConfig } from 'axios';

import { handleGenericAPIError, handleGenericError } from './factories/notificationFactory';

export const initAxios = (app, store) => {
  const storeRef = store;

  app.config.errorHandler = (err, vm, info) => {
    // `info` is a Vue-specific error info, e.g. which lifecycle hook
    // the error was found in. Only available in 2.2.0+
    // console.log('Vue errorHandler ' + err.message + ' \n ' + info + ' \n ' + err.stack);
    const msg = err.message ? err.message : err;
    const errStack = err.stack
      ? err.stack
      : 'No error stack available, please let the EnviDat team know of this Error!';

    console.error(`err: ${msg} . Error Stack: ${errStack}`);

    handleGenericError(storeRef, msg, info, errStack);
  };

  // Vue.config.warnHandler = function (msg, vm, trace) {
  //   // `trace` is the component hierarchy trace
  //   console.log('Vue.config.warnHandler vm: ' + vm.$store + ' ' + msg + ' \n\n ' + trace);
  //   // vm.$store.commit(ADD_USER_NOTIFICATION, msg + ' ' + trace );
  // }

  const excludedDomains = [
    process.env.VITE_STATIC_ROOT,
    process.env.VITE_CONFIG_URL,
    process.env.VITE_ZHDK_CLOUD_HOST,
    'https://envicloud.wsl.ch/',
    'https://os.unil.cloud.switch.ch',
  ].filter(Boolean);

  // matches the full excluded value as a substring (legacy behaviour) or as a
  // proper hostname/subdomain (fixes e.g. forest3dtwin.<host> not matching <host>)
  const isUrlExcluded = (url: string) => {
    let hostname = '';
    try {
      hostname = new URL(url).hostname;
    } catch {
      // relative/invalid URL, fall back to substring checks only
    }

    return excludedDomains.some((domain) => {
      if (url.includes(domain)) return true;

      let domainHost = domain;
      try {
        domainHost = new URL(domain).hostname;
      } catch {
        // domain is already a bare hostname (e.g. VITE_ZHDK_CLOUD_HOST)
      }

      return !!hostname && (hostname === domainHost || hostname.endsWith(`.${domainHost}`));
    });
  };

  axios.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      // Do something before request is sent

      const urlIsExcluded = isUrlExcluded(config.url);

      if (!urlIsExcluded) {
        config.withCredentials = true;
      }

      return config;
    },
    (error) =>
      // Do something with request error
      Promise.reject(error),
  );

  axios.interceptors.response.use(
    // this is called "onFulfilled"
    // Any status code that lie within the range of 2xx cause this function to trigger
    // Do something with response data
    // console.log('interceptor got ' + response.status);
    (response) => response,
    (error) => {
      // this is called "onRejected"
      // console.log('interceptor error ' + error);
      if (error.status >= 500) {
        handleGenericAPIError(store, error);
      }
      // Any status codes that falls outside the range of 2xx cause this function to trigger
      // Do something with response error
      // throw new Error(error);
      return Promise.reject(error);
    },
  );
};
